import ApiError from "@/model/ApiError";
import ErrorResponse from "@/model/ErrorResponse";
import API_BASE_URL from "@/config/apiConfig";

async function handleResponse<TResponse>(
  response: Response
): Promise<TResponse> {
  if (!response.ok) {
    const errorBody = (await response
      .json()
      .catch(() => null)) as ErrorResponse | null;
    throw new ApiError(
      response.status,
      errorBody?.message ?? response.statusText
    );
  }

  return response.json() as Promise<TResponse>;
}

function authHeaders(accessToken?: string): Record<string, string> {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

async function getJson<TResponse>(
  path: string,
  accessToken?: string
): Promise<TResponse> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    headers: authHeaders(accessToken),
  });
  return handleResponse<TResponse>(response);
}

async function postJson<TResponse>(
  path: string,
  body: unknown,
  accessToken?: string
): Promise<TResponse> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(accessToken),
    },
    body: JSON.stringify(body),
  });
  return handleResponse<TResponse>(response);
}

async function putJson<TResponse>(
  path: string,
  body: unknown,
  accessToken?: string
): Promise<TResponse> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(accessToken),
    },
    body: JSON.stringify(body),
  });
  return handleResponse<TResponse>(response);
}

async function deleteNoContent(
  path: string,
  accessToken?: string
): Promise<void> {
  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: "DELETE",
    headers: authHeaders(accessToken),
  });
  if (!response.ok) {
    const errorBody = (await response
      .json()
      .catch(() => null)) as ErrorResponse | null;
    throw new ApiError(
      response.status,
      errorBody?.message ?? response.statusText
    );
  }
}

const httpClient = {
  getJson,
  postJson,
  putJson,
  deleteNoContent,
};

export default httpClient;
